// Import the authenticated upload function
import { api } from './api.util';
// Import centralized API configuration
import { API_URL } from '../config/api.config';

const API_BASE_URL = API_URL;

/**
 * Get auth token from localStorage
 * @returns {string|null} Auth token
 */
const getAuthToken = () => {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.token;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      return null;
    }
  }
  return null;
};

/**
 * Get headers with authentication
 * @returns {Object} Headers object
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

/**
 * Get auth headers without content-type (for FormData uploads)
 * @returns {Object} Headers object
 */
const getAuthHeadersNoContentType = () => {
  const token = getAuthToken();
  return {
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// Helper function to make authenticated API requests
const makeApiRequest = async (url, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// =============================================================================
// EXAM MANAGEMENT
// =============================================================================

export const getExams = async () => {
  return makeApiRequest('/exams');
};

export const getExamById = async (examId) => {
  return makeApiRequest(`/exams/${examId}`);
};

export const createExam = async (examData) => {
  return makeApiRequest('/exams', {
    method: 'POST',
    body: JSON.stringify(examData),
  });
};

export const updateExam = async (examId, examData) => {
  return makeApiRequest(`/exams/${examId}`, {
    method: 'PUT',
    body: JSON.stringify(examData),
  });
};

export const deleteExam = async (examId) => {
  return makeApiRequest(`/exams/${examId}`, {
    method: 'DELETE',
  });
};

// =============================================================================
// SUBJECT MANAGEMENT
// =============================================================================

export const getAllSubjects = async () => {
  return makeApiRequest('/subjects');
};

export const getSubjectsByExam = async (examId) => {
  return makeApiRequest(`/exams/${examId}/subjects`);
};

export const getSubjectById = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}`);
};

export const createSubject = async (examId, subjectData) => {
  // If examId is provided, create subject through exam route
  if (examId) {
    return makeApiRequest(`/exams/${examId}/subjects`, {
      method: 'POST',
      body: JSON.stringify(subjectData),
    });
  }
  // If no examId, create subject directly (without requiring an exam)
  return makeApiRequest('/subjects', {
    method: 'POST',
    body: JSON.stringify(subjectData),
  });
};

export const updateSubjectPricingForExam = async (subjectId, examId, pricingData) => {
  return makeApiRequest(`/subjects/${subjectId}/pricing/${examId}`, {
    method: 'PUT',
    body: JSON.stringify(pricingData),
  });
};

export const updateSubject = async (subjectId, subjectData) => {
  return makeApiRequest(`/subjects/${subjectId}`, {
    method: 'PUT',
    body: JSON.stringify(subjectData),
  });
};

export const deleteSubject = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}`, {
    method: 'DELETE',
  });
};

// =============================================================================
// VIDEO MANAGEMENT
// =============================================================================

export const getVideosBySubject = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}/videos`);
};

export const getVideoById = async (videoId) => {
  return makeApiRequest(`/videos/${videoId}`);
};

export const createVideo = async (subjectId, videoData) => {
  return makeApiRequest(`/subjects/${subjectId}/videos`, {
    method: 'POST',
    body: JSON.stringify(videoData),
  });
};

export const updateVideo = async (videoId, videoData) => {
  return makeApiRequest(`/videos/${videoId}`, {
    method: 'PUT',
    body: JSON.stringify(videoData),
  });
};

export const updateVideoWithFile = async (videoId, formData) => {
  try {
    // Use PUT method for updating existing video
    const response = await api.upload(`/videos/${videoId}/upload`, formData, 'PUT');

    if (!response.success) {
      throw new Error(response.message || 'Video update failed');
    }

    return response;
  } catch (error) {
    console.error('Error updating video with file:', error);
    throw error;
  }
};

export const deleteVideo = async (videoId) => {
  return makeApiRequest(`/videos/${videoId}`, {
    method: 'DELETE',
  });
};

export const uploadVideo = async (subjectId, formData) => {
  try {
    const response = await api.upload(`/videos/upload`, formData);

    if (!response.success) {
      throw new Error(response.message || 'Video upload failed');
    }

    return response;
  } catch (error) {
    console.error('Video upload failed:', error);
    throw error;
  }
};

// =============================================================================
// MATERIAL ENTITY (exam-like) MANAGEMENT
// =============================================================================

export const getMaterials = async () => {
  return makeApiRequest('/materials');
};

export const getMaterialsBySubject = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}/materials`);
};

export const getMaterialById = async (materialId) => {
  return makeApiRequest(`/materials/${materialId}`);
};

export const createMaterial = async (materialData) => {
  // Safety check - ensure materialData is defined
  if (!materialData) {
    throw new Error('Material data is required');
  }
  
  // Materials are created directly via /api/materials endpoint
  // Materials don't have file uploads (they're packages that contain subjects)
  return makeApiRequest(`/materials`, {
    method: 'POST',
    body: JSON.stringify(materialData),
  });
};

export const updateMaterial = async (materialId, materialData) => {
  return makeApiRequest(`/materials/${materialId}`, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  });
};

export const deleteMaterial = async (materialId) => {
  return makeApiRequest(`/materials/${materialId}`, {
    method: 'DELETE',
  });
};

export const addSubjectToMaterial = async (materialId, subjectData) => {
  return makeApiRequest(`/materials/${materialId}/subjects`, {
    method: 'POST',
    body: JSON.stringify(subjectData),
  });
};

// =============================================================================
// PDF MANAGEMENT
// =============================================================================

export const getPdfsBySubject = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}/pdfs`);
};

export const getMockTestPdfsBySubject = async (subjectId) => {
  return makeApiRequest(`/subjects/${subjectId}/pdfs?isMockTest=true`);
};

export const getPdfById = async (pdfId) => {
  return makeApiRequest(`/pdfs/${pdfId}`);
};

export const createPdf = async (subjectId, pdfData) => {
  return makeApiRequest(`/subjects/${subjectId}/pdfs`, {
    method: 'POST',
    body: JSON.stringify(pdfData),
  });
};

export const uploadPdf = async (subjectId, formData) => {
  const response = await fetch(`${API_BASE_URL}/subjects/${subjectId}/pdfs/upload`, {
    method: 'POST',
    headers: getAuthHeadersNoContentType(),
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.message || 'PDF upload failed');
  return data;
};

export const updatePdf = async (pdfId, pdfData) => {
  return makeApiRequest(`/pdfs/${pdfId}`, {
    method: 'PUT',
    body: JSON.stringify(pdfData),
  });
};

export const updatePdfWithFile = async (pdfId, formData) => {
  const response = await fetch(`${API_BASE_URL}/pdfs/${pdfId}/upload`, {
    method: 'PUT',
    headers: getAuthHeadersNoContentType(),
    body: formData,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.message || 'PDF update failed');
  return data;
};

export const deletePdf = async (pdfId) => {
  return makeApiRequest(`/pdfs/${pdfId}`, {
    method: 'DELETE',
  });
};

// =============================================================================
// MOCK TEST PACKAGE MANAGEMENT (exam-like packages)
// =============================================================================

export const getMockTests = async () => {
  return makeApiRequest('/mock-tests');
};

export const getMockTestsBySubject = async (subjectId) => {
  return makeApiRequest(`/mock-tests?subject=${subjectId}`);
};

export const getMockTestById = async (mockTestId) => {
  return makeApiRequest(`/mock-tests/${mockTestId}`);
};

export const createMockTest = async (mockTestData) => {
  return makeApiRequest(`/mock-tests`, {
    method: 'POST',
    body: JSON.stringify(mockTestData),
  });
};

export const updateMockTest = async (mockTestId, mockTestData) => {
  return makeApiRequest(`/mock-tests/${mockTestId}`, {
    method: 'PUT',
    body: JSON.stringify(mockTestData),
  });
};

export const deleteMockTest = async (mockTestId) => {
  return makeApiRequest(`/mock-tests/${mockTestId}`, {
    method: 'DELETE',
  });
};

export const getSubjectsByMockTest = async (mockTestId) => {
  return makeApiRequest(`/mock-tests/${mockTestId}/subjects`);
};

export const addSubjectToMockTest = async (mockTestId, subjectData) => {
  return makeApiRequest(`/mock-tests/${mockTestId}/subjects`, {
    method: 'POST',
    body: JSON.stringify(subjectData),
  });
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const getContentStats = async () => {
  try {
    const [examsResponse, subjectsResponse, videosResponse, materialsResponse, testsResponse] = await Promise.all([
      makeApiRequest('/exams'),
      makeApiRequest('/subjects'),
      makeApiRequest('/videos'),
      makeApiRequest('/materials'),
      makeApiRequest('/mock-tests')
    ]);

    return {
      totalExams: examsResponse.count || examsResponse.data?.length || 0,
      totalSubjects: subjectsResponse.count || subjectsResponse.data?.length || 0,
      totalVideos: videosResponse.count || videosResponse.data?.length || 0,
      totalMaterials: materialsResponse.count || materialsResponse.data?.length || 0,
      totalTests: testsResponse.count || testsResponse.data?.length || 0,
    };
  } catch (error) {
    console.error('Error fetching content stats:', error);
    return {
      totalExams: 0,
      totalSubjects: 0,
      totalVideos: 0,
      totalMaterials: 0,
      totalTests: 0,
    };
  }
};

export const searchContent = async (query, type = 'all') => {
  try {
    const searchParams = new URLSearchParams({
      q: query,
      type,
    });

    return makeApiRequest(`/search?${searchParams.toString()}`);
  } catch (error) {
    console.error('Search failed:', error);
    return { data: [], count: 0 };
  }
};

// File upload helper
export const uploadFile = async (file, metadata = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata
    Object.keys(metadata).forEach(key => {
      formData.append(key, metadata[key]);
    });

    const response = await api.upload('/upload', formData);
    
    if (!response.success) {
      throw new Error(response.message || 'File upload failed');
    }

    return response;
  } catch (error) {
    console.error('File upload failed:', error);
    throw error;
  }
};